import dayjs from "dayjs"

import { downloadFile } from "@lib/helperFile"
import type {
  IAgingByStage,
  IBacklogBatchPreview,
  IContractPeriodMatrix,
  ICreateBacklogBatchInput,
  ICreateBacklogBatchResult,
  ICurrentPeriodStatus,
  IDuplicateNotifications,
  IFeeCalendarFilterInput,
  IFeeCalendarOutput,
  IFeeMatrixFilterInput,
  IFeeNotificationOverview,
  IFeeNotificationOverviewFilterInput,
  INotificationBacklogFilterInput,
  INotificationBacklogRow,
  ISendPreflight,
  IUnreachableContracts,
  IUnsentBadgeCount,
} from "@models/feeNotificationReport/feeNotificationReportModel"
import type { PagedResultDto } from "@services/dto/pagedResultDto"

import http, { type ApiRequestConfig } from "../httpService"

/**
 * Req10.4 — tên file xuất gồm loại danh sách + kỳ + timestamp.
 *
 * `services/feeNotificatiion/feeNotificationService.tsx` giữ helper cùng tên ở module scope và
 * **không export** nó, nên đây là bản sao đúng công thức thay vì import chéo. Đổi công thức thì
 * phải sửa cả hai chỗ.
 */
const buildExportFileName = (prefix: string, period?: string): string => {
  const periodPart = period ? period.replace(/\//g, "") : ""
  const timestamp = dayjs().format("YYYYMMDD_HHmm")
  return `${[prefix, periodPart, timestamp].filter(Boolean).join("_")}.xlsx`
}

/**
 * Báo cáo Thông báo phí theo dự án (spec `fee-notification-reporting`).
 * ABP dynamic API cho đường đọc, `api/Export/*` (blob) cho đường xuất Excel — đúng chuỗi 4 chặng
 * `DataTable exportExcel → store → http.get(responseType:"blob") → downloadFile` (Req10.1).
 */
class FeeNotificationReportService {
  /** Ma trận Hợp đồng × Kỳ phí, phân trang theo hợp đồng (Req 4.1). */
  public async getContractPeriodMatrix(
    params: IFeeMatrixFilterInput
  ): Promise<IContractPeriodMatrix> {
    const res = await http.get(
      "api/services/app/FeeNotificationReport/GetContractPeriodMatrix",
      { params }
    )
    return res.data.result
  }

  /** Xuất ma trận theo ĐÚNG bộ lọc + dải kỳ đang xem (Req10.3). */
  public async exportMatrix(params: IFeeMatrixFilterInput): Promise<void> {
    const res = await http.get("api/Export/ExportFeeNotificationMatrix", {
      params,
      responseType: "blob",
    })
    downloadFile(
      res.data,
      buildExportFileName("MaTranThongBaoPhi", params?.fromPeriod)
    )
  }

  /**
   * Dashboard Thông báo phí theo dự án (Req 3): KPI + 4 khối phân tích trong MỘT request.
   * Server đã làm tròn/tổng hợp sẵn; FE chỉ định dạng — mọi tỉ lệ dùng sentinel `-1`.
   */
  public async getFeeNotificationOverview(
    params: IFeeNotificationOverviewFilterInput
  ): Promise<IFeeNotificationOverview> {
    const res = await http.get(
      "api/services/app/FeeNotificationReport/GetFeeNotificationOverview",
      { params }
    )
    return res.data.result
  }

  /**
   * Dải trạng thái kỳ hiện tại (Req 12). Chỉ nhận `projectId` — kỳ do server chốt theo UTC+07 để
   * FE không tự suy "tháng hiện tại" bằng múi giờ máy người dùng.
   */
  public async getCurrentPeriodStatus(
    projectId: number
  ): Promise<ICurrentPeriodStatus> {
    const res = await http.get(
      "api/services/app/FeeNotificationReport/GetCurrentPeriodStatus",
      { params: { projectId } }
    )
    return res.data.result
  }

  /** Xuất dashboard theo đúng bộ lọc đang xem (Req10.3, api-contract §12). */
  public async exportOverview(
    params: IFeeNotificationOverviewFilterInput
  ): Promise<void> {
    const res = await http.get("api/Export/ExportFeeNotificationOverview", {
      params,
      responseType: "blob",
    })
    downloadFile(
      res.data,
      buildExportFileName("TongQuanThongBaoPhi", params?.fromPeriod)
    )
  }

  /* ────────────── Backlog chưa thông báo + tạo đợt (Req 6, api-contract §3, §4) ────────────── */

  /** Ô có dư nợ + đã quá hạn + chưa từng gửi thành công; server sort dư nợ giảm dần (Req 6.1). */
  public async getNotificationBacklog(
    params: INotificationBacklogFilterInput
  ): Promise<PagedResultDto<INotificationBacklogRow>> {
    const res = await http.get(
      "api/services/app/FeeNotificationReport/GetNotificationBacklog",
      { params }
    )
    return res.data.result
  }

  /**
   * Xem trước lệnh tạo đợt (Req 6.5, 6.6, 6.8, 6.9) — **đọc-only**.
   *
   * Endpoint tên `Get*` nên ABP map nó thành **GET**: tập ô đi qua query string (`qs` bracket
   * style, ASP.NET Core chuẩn hoá bằng `JQueryQueryStringValueProvider`), KHÔNG phải body. Vì
   * request-line của Kestrel giới hạn 8KB, số ô mỗi lần xem trước bị chặn ở tầng UI —
   * `BACKLOG_MAX_SELECTION` trong `scenes/feeNotificationReport/backlog/backlogBatch.ts`.
   */
  public async getBacklogBatchPreview(
    input: ICreateBacklogBatchInput
  ): Promise<IBacklogBatchPreview> {
    const res = await http.get(
      "api/services/app/FeeNotificationReport/GetBacklogBatchPreview",
      { params: input }
    )
    return res.data.result
  }

  /**
   * Đường **GHI duy nhất** của spec: tạo một đợt cho mỗi kỳ trong tập chọn, **luôn** ở
   * `Status = NEW` (Req 6.4, 11.1). Không có nhánh nào gửi — người dùng phải bấm Gửi ở màn đợt.
   * Tên `Create*` ⇒ ABP map POST (body-bound), nên tập ô không bị giới hạn độ dài URL.
   *
   * Toast thành công do màn gọi hiển thị (kèm số đợt đã tạo), không đặt ở service vì thông điệp
   * phải nói rõ "đợt ở trạng thái Tạo mới, cần bấm Gửi".
   */
  public async createBacklogBatch(
    input: ICreateBacklogBatchInput
  ): Promise<ICreateBacklogBatchResult> {
    const res = await http.post(
      "api/services/app/FeeNotificationReport/CreateBacklogBatch",
      input
    )
    return res.data.result
  }

  /** Xuất backlog theo đúng bộ lọc đang xem (Req10.1, 10.3). */
  public async exportBacklog(
    params: INotificationBacklogFilterInput
  ): Promise<void> {
    const res = await http.get("api/Export/ExportNotificationBacklog", {
      params,
      responseType: "blob",
    })
    downloadFile(
      res.data,
      buildExportFileName("BacklogThongBaoPhi", params?.fromPeriod)
    )
  }

  /* ────────────── Hợp đồng không gửi được (Req 7, api-contract §5) ────────────── */

  /** Một dòng / một hợp đồng với **lý do trội**, kèm số lượng theo từng lý do (Req 7.3). */
  public async getUnreachableContracts(
    params: IFeeMatrixFilterInput
  ): Promise<IUnreachableContracts> {
    const res = await http.get(
      "api/services/app/FeeNotificationReport/GetUnreachableContracts",
      { params }
    )
    return res.data.result
  }

  /** Xuất danh sách không gửi được để giao cho bộ phận dữ liệu (Req 7.5). */
  public async exportUnreachableContracts(
    params: IFeeMatrixFilterInput
  ): Promise<void> {
    const res = await http.get("api/Export/ExportUnreachableContracts", {
      params,
      responseType: "blob",
    })
    downloadFile(
      res.data,
      buildExportFileName("HopDongKhongGuiDuoc", params?.fromPeriod)
    )
  }

  /* ────────────── Aging × bậc đã nhắc (Req 8, api-contract §6) ────────────── */

  /** Cross-tab **thưa**: chỉ tổ hợp (nhóm tuổi, bậc) có hợp đồng mới có ô. */
  public async getAgingByReminderStage(
    params: IFeeMatrixFilterInput
  ): Promise<IAgingByStage> {
    const res = await http.get(
      "api/services/app/FeeNotificationReport/GetAgingByReminderStage",
      { params }
    )
    return res.data.result
  }

  public async exportAgingByReminderStage(
    params: IFeeMatrixFilterInput
  ): Promise<void> {
    const res = await http.get("api/Export/ExportAgingByReminderStage", {
      params,
      responseType: "blob",
    })
    downloadFile(
      res.data,
      buildExportFileName("TuoiNoTheoBacNhac", params?.fromPeriod)
    )
  }

  /* ────────────── Cảnh báo gửi trùng (Req 9, api-contract §7) ────────────── */

  /**
   * Hai danh sách trong MỘT response: (HĐ, kỳ, loại) gửi > 1 lần và các trường hợp nhiều log cùng
   * một địa chỉ email. Server chỉ nhận **một** cặp `skipCount`/`maxResultCount` nên hai danh sách
   * phân trang cùng nhau.
   */
  public async getDuplicateNotifications(
    params: IFeeMatrixFilterInput
  ): Promise<IDuplicateNotifications> {
    const res = await http.get(
      "api/services/app/FeeNotificationReport/GetDuplicateNotifications",
      { params }
    )
    return res.data.result
  }

  public async exportDuplicateNotifications(
    params: IFeeMatrixFilterInput
  ): Promise<void> {
    const res = await http.get("api/Export/ExportDuplicateNotifications", {
      params,
      responseType: "blob",
    })
    downloadFile(
      res.data,
      buildExportFileName("CanhBaoGuiTrung", params?.fromPeriod)
    )
  }

  /* ────────────── Đèn báo header (Req 13, api-contract §9) ────────────── */

  /**
   * Số HĐ có dư nợ quá hạn mà chưa gửi thành công, phạm vi **một dự án đang ghim** (Req 13.3).
   * Server trả kèm `severity` để FE không tự suy màu.
   */
  public async getUnsentBadgeCount(
    projectId: number
  ): Promise<IUnsentBadgeCount> {
    // Đèn báo header nạp lại theo chu kỳ 60s ở nền — giống `metricsService.getHeaderMetrics`,
    // không kéo thanh progress để màn hình rảnh không bị nháy định kỳ.
    const config: ApiRequestConfig = {
      params: { projectId },
      skipProgressBar: true,
    }
    const res = await http.get(
      "api/services/app/FeeNotificationReport/GetUnsentBadgeCount",
      config
    )
    return res.data.result
  }

  /* ────────────── Preflight trước khi gửi (Req 14, api-contract §10) ────────────── */

  /**
   * Preflight của đường **gửi cả đợt** — đọc-only tuyệt đối: không gửi, không đổi trạng thái đợt,
   * không enqueue job nào (Req 11.1). Đường gửi lại một khách KHÔNG đi qua đây (Req 14.8).
   */
  public async getSendPreflight(batchId: number): Promise<ISendPreflight> {
    const res = await http.get(
      "api/services/app/FeeNotificationReport/GetSendPreflight",
      { params: { batchId } }
    )
    return res.data.result
  }

  /* ────────────── Lịch thu phí (design `fee-notification-calendar` §4.3) ────────────── */

  /**
   * Lịch thu phí theo **cửa sổ ngày** của view đang xem — một request trả CẢ hai chế độ hiển thị
   * (`days` aggregate + `installments` chi tiết) cộng lane "Quá hạn mang sang".
   *
   * `fromDate`/`toDate` phải là `"YYYY-MM-DD"` (ngày lịch VN, không phần giờ) — xem
   * `IFeeCalendarFilterInput`. `projectId <= 0` ⇒ server trả rỗng và không chạm DB (design §7.1),
   * nên màn gọi vẫn phải tự gác để không gọi vô ích.
   */
  public async getFeeCalendar(
    params: IFeeCalendarFilterInput
  ): Promise<IFeeCalendarOutput> {
    const res = await http.get(
      "api/services/app/FeeNotificationReport/GetFeeCalendar",
      { params }
    )
    return res.data.result
  }

  /**
   * Xuất lịch thu phí theo ĐÚNG bộ lọc + cửa sổ ngày đang xem (Req 10.3) — route thứ 7 của họ
   * `api/Export/*`, khớp `ExportFeeCalendarAsync` của AppService.
   *
   * Khác 6 export còn lại ở phần kỳ trong tên file: lịch không có `fromPeriod`, nên đoạn giữa lấy
   * ngày đầu cửa sổ đã bỏ dấu `-` (`20260801`) — cùng vai trò định danh lần xuất, không phải nhãn kỳ.
   */
  public async exportFeeCalendar(
    params: IFeeCalendarFilterInput
  ): Promise<void> {
    const res = await http.get("api/Export/ExportFeeCalendar", {
      params,
      responseType: "blob",
    })
    downloadFile(
      res.data,
      buildExportFileName("LichThuPhi", params?.fromDate?.replace(/-/g, ""))
    )
  }
}

export default new FeeNotificationReportService()
